import { schedulePaint } from './paint'
import {
    setUndoImageSource,
    setNewGameButtonImageSource,
    setMenuImageSource,
    setCardBackImageSource,
    setCardState,
    appState,
    resetCardState,
} from './state'
import { playSound } from './audioFunctions'
import { DEFAULT_DECK } from './assets/constants'
import { assignCardCoordinates } from './assignCardCoordinates'
import { Card } from '../Models/Card'
import { timerReset, timerIsStarted } from './timer'
import { recordGameLoss } from './statisticsManager'
import { stopWinAnimation } from './winAnimation'
import { CurrentAppState } from '../Models/AppState'
import { isLikelyWinnable } from './dealHeuristics'

/**
 * Mulberry32 — a fast 32-bit seeded PRNG.
 * Returns a function that produces deterministic floats in [0, 1) from a numeric seed.
 */
function mulberry32(seed: number): () => number {
    let s = seed | 0
    return () => {
        s = (s + 0x6d2b79f5) | 0
        let t = Math.imul(s ^ (s >>> 15), 1 | s)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

/**
 * Converts a string seed to a numeric hash (djb2).
 */
function hashSeed(str: string): number {
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
    }
    return hash >>> 0
}

/**
 * Normalise any seed input to a positive integer.
 */
export function normaliseSeed(input: string | number): number {
    if (typeof input === 'number') return Math.abs(Math.floor(input)) || 1
    const n = Number(input)
    if (!isNaN(n) && isFinite(n)) return Math.abs(Math.floor(n)) || 1
    return hashSeed(input)
}

export function deal(deck) {
    const tableau: [Card[], Card[], Card[], Card[], Card[], Card[], Card[]] = [[], [], [], [], [], [], []]
    const stock = [...deck].reverse()
    // Move hand cards to tableau. Set the last card in each tableau to be face up
    for (let i = 0; i < 7; i++) {
        if (!tableau[i]) {
            tableau[i] = []
        }
        const activeTableau = tableau[i]
        for (let j = 0; j <= i; j++) {
            activeTableau.push(stock.pop())
            if (j === i) {
                activeTableau[activeTableau.length - 1].isFaceDown = false
            }
        }
    }
    // We now know the rest of the cards in hand will remain face up cards
    for (let k = 0; k < stock.length; k++) {
        stock[k].isFaceDown = true
    }
    const deckWithCoordinates = assignCardCoordinates({ stock, tableau })
    // hand contains all waste and stock
    setCardState(deckWithCoordinates)
}

/**
 * Fisher–Yates shuffle in O(n) time.
 * When a seed is provided, uses a deterministic PRNG so the same seed always
 * produces the same card order — useful for testing and reproducible games.
 *
 * When `favorWinnable` is true the deck is checked against heuristic filters
 * after each shuffle.  If the deal looks likely unwinnable we re-shuffle
 * (up to a hard cap) to find a better arrangement.  Because each retry uses
 * a fresh `Math.random()` call the results never cycle.
 *
 * @param seed            Optional numeric seed for deterministic shuffle
 * @param favorWinnable   When true, reject likely-unwinnable deals
 * @return Shuffled copy of DEFAULT_DECK
 */
export function shuffle(seed?: number, favorWinnable = true) {
    playSound('shuffle')

    const MAX_ATTEMPTS = 50 // hard cap — never spin for more than this

    const rng = seed != null ? mulberry32(seed) : Math.random

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const deck = DEFAULT_DECK.map((card) => ({
            ...card,
            isFaceDown: true,
        }))
        for (let i = deck.length - 1; i > 0; i--) {
            const randomIdx = Math.floor(rng() * (i + 1))
            const temp = deck[i]

            deck[i] = deck[randomIdx]
            deck[randomIdx] = temp
        }

        // If heuristic filtering is off, or the deal passes, return immediately
        if (!favorWinnable || isLikelyWinnable(deck)) {
            return deck
        }
        // Otherwise loop and try a fresh shuffle (seeded RNG advances state
        // automatically so even deterministic seeds won't repeat)
    }

    // Exhausted attempts — return the last shuffle rather than blocking forever
    const deck = DEFAULT_DECK.map((card) => ({
        ...card,
        isFaceDown: true,
    }))
    for (let i = deck.length - 1; i > 0; i--) {
        const randomIdx = Math.floor(rng() * (i + 1))
        const temp = deck[i]

        deck[i] = deck[randomIdx]
        deck[randomIdx] = temp
    }
    return deck
}

export function setButtonImages(): void {
    // Setup Undo Button
    const undoImage = new Image()
    undoImage.src = `./assets/buttons/${appState.currentTheme.buttonNames}/undo.svg`

    // Setup New Game Button
    const newGameButton = new Image()
    newGameButton.src = `./assets/buttons/${appState.currentTheme.buttonNames}/plus.svg`

    // Setup Menu Button
    const menuButton = new Image()
    menuButton.src = `./assets/buttons/${appState.currentTheme.buttonNames}/menu.svg`

    setUndoImageSource(undoImage)
    setMenuImageSource(menuButton)
    setNewGameButtonImageSource(newGameButton)
}

export function loadCardBackImage(): Promise<void> {
    return new Promise((resolve) => {
        const cardBackImage = new Image()
        cardBackImage.src = `./assets/cardbacks/${appState.currentTheme.cardBackName}.svg`

        cardBackImage.onload = () => {
            setCardBackImageSource(cardBackImage)
            resolve()
        }

        cardBackImage.onerror = () => {
            console.error(`Failed to load card back image: ${cardBackImage.src}`)
            // Still set it even if it fails (will fall back to solid color)
            setCardBackImageSource(cardBackImage)
            resolve()
        }
    })
}

/**
 * Starts a new game by resetting state, shuffling, and dealing.
 * @param seed  Optional seed for a deterministic deal (dev/testing)
 */
export function startNewGame(seed?: number): void {
    // Stop any running win animation
    stopWinAnimation()

    // If the timer was running (player made at least one move), record a loss
    // (unless they already won — in which case the timer is already stopped)
    if (timerIsStarted()) {
        recordGameLoss()
    }
    timerReset()

    // Reset app state to Playing (may be Completed or Menu)
    appState.currentAppState = CurrentAppState.Playing

    resetCardState()
    // Seeded deals stay unfiltered so a seed always reproduces the same hand;
    // random deals always use the winnable-deal filter.
    const shuffledDeck = shuffle(seed, seed == null)
    deal(shuffledDeck)
    schedulePaint()
}
